/* One first-party preference authority. Loaded before any vendor bootstrap. */
(() => {
  'use strict';
  const lifetime = 180 * 24 * 60 * 60;
  const disabled = () => ({ analytics: false, advertising: false });
  function parse(raw, now = Math.floor(Date.now() / 1000)) {
    if (raw === null) return { analytics: true, advertising: true };
    const legacy = /^[\t-\r ]*1[\t-\r ]*\|[\t-\r ]*(granted|denied)[\t-\r ]*$/i.exec(raw);
    if (legacy && legacy[0] === raw) return { analytics: legacy[1].toLowerCase() === 'granted', advertising: legacy[1].toLowerCase() === 'granted' };
    const match = /^2\|([0-9]{1,12})\|([01])\|([01])$/.exec(raw);
    if (!match || match[0] !== raw) return disabled();
    const issued = Number(match[1]);
    if (issued <= 0 || issued > now || now - issued >= lifetime) return disabled();
    return { analytics: match[2] === '1', advertising: match[3] === '1' };
  }
  function readRaw() {
    try {
      const part = document.cookie.split(';').map(x => x.trim()).find(x => x.startsWith('strx_consent='));
      return part ? decodeURIComponent(part.slice(13)) : null;
    } catch (_) { return ''; }
  }
  let raw = readRaw();
  let current = parse(raw);
  let stopped = false;
  let fullyBlocked = false;
  let lostPreference = false;
  let expiry;
  const flag = value => value ? 'granted' : 'denied';
  const publish = () => {
    window.__strxConsent = flag(current.analytics);
    window.__strxAdvertising = flag(current.advertising);
  };

  // A loaded SDK may upload on stop/unload or restart cookieless. This additional
  // CSP applies synchronously BEFORE calling any SDK or beginning navigation.
  // It cannot be loosened in the current document; changes resume on a fresh page.
  function barrier(navigationOnly = false) {
    if (stopped && (navigationOnly || fullyBlocked)) return;
    stopped = true;
    fullyBlocked = !navigationOnly;
    // Navigation preserves the other vendors' permitted unload events and the
    // replay session cookie. Opt-out adds the stronger policy even if a previous
    // navigation policy is already present in a restored document.
    const google = navigationOnly && current.analytics
      ? ' https://*.google-analytics.com https://*.analytics.google.com https://analytics.google.com https://*.googletagmanager.com https://*.google.com' : '';
    const ads = navigationOnly && current.advertising
      ? ' https://*.facebook.com https://*.facebook.net https://*.tiktok.com https://*.tiktokv.com' : '';
    const permitted = google + ads;
    // Only loaded/requested SDKs need an egress barrier. Excluded account and
    // settings pages must keep working when a preference changes in another tab.
    if (['__strxClarityRequested', '__strxGtagRequested', '__strxFbeventsRequested', '__strxTtqRequested'].some(key => window[key])) {
      const policy = document.createElement('meta');
      policy.httpEquiv = 'Content-Security-Policy';
      // Preserve Alpine's existing runtime evaluation and native form redirects
      // (checkout POST -> Tap). This extra policy restricts SDK transports; it is
      // not the site's XSS policy and cannot loosen an existing header policy.
      // frame-src keeps the Saudi Business Center seal (a lazy footer iframe, not
      // a tracker) loadable after opt-out or a back/forward-cache restore; the
      // origin mirrors SbcSeal.Origin (a unit test keeps the two in sync).
      policy.content = "connect-src 'self'" + permitted + "; img-src 'self' data: blob: https://strixon.b-cdn.net" + permitted
        + "; script-src 'self' 'unsafe-inline' 'unsafe-eval'" + permitted
        + "; frame-src 'self' https://eauthenticate.saudibusiness.gov.sa; worker-src 'none'";
      document.head.appendChild(policy);
    }
    window.__strxTrackingStopped = true;
    if (!navigationOnly && window.__strxGa4Id) window['ga-disable-' + window.__strxGa4Id] = true;
    try { window.__strxStopClarity?.(!navigationOnly); } catch (_) { /* CSP is authoritative */ }
  }
  function clearCookies() {
    const scopes = [''];
    const parts = location.hostname.split('.');
    for (let i = 0; i < parts.length - 1; i++) scopes.push('; Domain=' + parts.slice(i).join('.'));
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie.split(';').forEach(pair => {
      const name = pair.split('=')[0].trim();
      const analyticsCookie = /^(_ga(?:_|$)|_gid$|_gat|_clck$|_clsk$)/.test(name);
      const advertisingCookie = /^(_gcl_|_fbp$|_fbc$|_ttp$|ttcsid)/.test(name);
      if ((!current.analytics && analyticsCookie) || (!current.advertising && advertisingCookie)) {
        scopes.forEach(scope => { document.cookie = name + '=; Path=/; Max-Age=0; SameSite=Lax' + scope + secure; });
      }
    });
  }
  function scheduleExpiry() {
    clearTimeout(expiry);
    const match = /^2\|([0-9]{1,12})\|/.exec(raw || '');
    if (match) {
      const delay = (Number(match[1]) + lifetime) * 1000 - Date.now();
      if (delay > 0) expiry = setTimeout(() => reconcile(true), Math.min(delay + 50, 2147483647));
    }
  }
  function reconcile(expired = false) {
    const nextRaw = readRaw();
    // A missing preference in an already-open document is a restriction, so
    // expiration/clearing cannot silently resume a running optional SDK.
    if (nextRaw === null && raw !== null) lostPreference = true;
    if (nextRaw !== null) lostPreference = false;
    const next = nextRaw === null && lostPreference ? disabled() : parse(nextRaw);
    const changed = current.analytics !== next.analytics || current.advertising !== next.advertising;
    if (changed) {
      if ((current.analytics && !next.analytics) || (current.advertising && !next.advertising)) barrier();
      current = next;
      publish();
      clearCookies();
      document.dispatchEvent(new CustomEvent('strx:consent-changed'));
      // Do not reload in-progress forms in another tab. All collection remains
      // blocked until the visitor's next full navigation.
    }
    raw = nextRaw;
    if (!expired || nextRaw !== null) scheduleExpiry();
  }
  function broadcast() {
    try { localStorage.setItem('strx.privacy.changed', String(Date.now())); } catch (_) { /* cookie polling remains available */ }
  }
  publish();
  scheduleExpiry();
  window.strxPrivacy = {
    parse, read: () => { reconcile(); return { ...current }; }, barrier, broadcast,
    allows: category => !stopped && !!current[category],
  };
  window.addEventListener('storage', event => { if (event.key === 'strx.privacy.changed') reconcile(); });
  window.addEventListener('focus', () => reconcile());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') reconcile(); });
  window.addEventListener('pagehide', () => {
    if (window.__strxClarityRequested) barrier(true);
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      reconcile();
      if (stopped || document.querySelector('[data-privacy-form]')) location.reload();
    }
  });
  // Cookie polling also covers changes made with JavaScript disabled in another tab.
  setInterval(() => { reconcile(); if (stopped) clearCookies(); }, 1000);
  document.addEventListener('DOMContentLoaded', () => {
    clearCookies();
    if (document.querySelector('[data-privacy-saved]')) broadcast();
  }, { once: true });
})();
